import { type CaseDto, executionHoldMessage, type MeDto, NEW_TASK_TYPES, TASK_TYPES, type TaskDto, type TaskType, todayInWarsaw } from '@bqcrm/shared';
import { Badge, Button, Card, Group, Modal, MultiSelect, Select, Stack, Text, TextInput, Textarea, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconCalendarEvent, IconCarOff, IconLock, IconPlus, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { TaskStatusBadge, TradeBadge } from '../../components/Badges';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { notifyError } from '../../notify';
import { useTrades, useTaskTypeLabel } from '../../data';
import { RealizationDialog } from '../planning/RealizationDialog';
import { usePeople, useTeams } from '../planning/common';
import { dayLong } from '../../format';
import { PlanDialog } from '../planning/PlanDialog';
import { TaskPanel } from '../planning/TaskPanel';
import { ClientPortalCard } from './ClientPortalCard';

function NewTask({ c, me, onCreated, onRealization, onClose }: { c: CaseDto; me: MeDto; onCreated: (t: TaskDto) => void; onRealization: () => void; onClose: () => void }) {
  const trades = useTrades();
  // pełne planowanie: uprawnienie albo przypisane zadanie „Zaplanuj realizację” w tej sprawie
  const full = can(me, 'planowanie.edycja', 'W') || c.can.planRealization;
  // realizacja (po umowie) — przez wybór branż; tu: wizja, pomiary i inne zadania z listą ogólną
  const types = NEW_TASK_TYPES.filter((t) => full || t === 'wizja' || t === 'pomiary');
  const [f, setF] = useState({ taskType: types[0]!, scopeIds: [] as string[], title: '', description: '' });
  const create = useMutation({
    mutationFn: () => api<TaskDto>('/tasks', { method: 'POST', body: { ...f, caseId: c.id } }),
    onSuccess: onCreated,
    onError: notifyError,
  });
  const tradeName = (code: string) => trades.data?.find((t) => t.code === code)?.name ?? code;
  return (
    <Modal opened onClose={onClose} title="Nowe zadanie">
      <Stack>
        <Select
          label="Rodzaj"
          data={types.map((t) =>
            t === 'montaz'
              ? { value: t, label: c.hold ? 'Montaż / realizacja — sprawa wstrzymana' : 'Montaż / realizacja po umowie — wybór branż…', disabled: !!c.hold }
              : { value: t, label: TASK_TYPES[t].label },
          )}
          value={f.taskType}
          allowDeselect={false}
          description={c.hold ? executionHoldMessage(c.hold.until) : undefined}
          onChange={(v) => {
            if (v === 'montaz') return onRealization();
            setF({ ...f, taskType: (v ?? 'wizja') as TaskType });
          }}
        />
        <MultiSelect label="Branże (opcjonalnie)" data={c.scopes.map((s) => ({ value: s.id, label: tradeName(s.tradeCode) }))} value={f.scopeIds} onChange={(v) => setF({ ...f, scopeIds: v })} />
        <TextInput label="Tytuł" placeholder="domyślnie: rodzaj i branże" value={f.title} onChange={(e) => setF({ ...f, title: e.currentTarget.value })} />
        <Textarea label="Opis dla ekipy" autosize minRows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.currentTarget.value })} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => create.mutate()} loading={create.isPending}>
            Utwórz i zaplanuj
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const WD_SHORT = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'];
const MONTH_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
const at = (d: string) => new Date(`${d}T12:00:00Z`);
/** „pon. 3.11” */
const shortDay = (d: string) => `${WD_SHORT[at(d).getUTCDay()]} ${at(d).getUTCDate()}.${String(at(d).getUTCMonth() + 1).padStart(2, '0')}`;
/** Dni wizyty: „poniedziałek, 3 listopada 2026” albo „pon. 3.11 – śr. 5.11.2026”. */
const visitDays = (from: string, to: string) => (from === to ? dayLong(from) : `${shortDay(from)} – ${shortDay(to)}.${to.slice(0, 4)}`);
const hours = (a: { timeFrom: string | null; timeTo: string | null }) => (a.timeFrom ? `${a.timeFrom}–${a.timeTo ?? ''}` : 'cały dzień');

/** Kafelek z datą: dzień tygodnia, dzień, miesiąc (kilka dni — zakres). */
function DateTile({ t }: { t: TaskDto }) {
  const box = { width: 76, flexShrink: 0, borderRadius: 8, textAlign: 'center' as const, padding: '6px 4px' };
  if (!t.dateFrom) {
    return (
      <div style={{ ...box, background: 'var(--mantine-color-default-hover)' }}>
        <Text size="xs" c="dimmed" lh={1.2}>
          bez
          <br />
          terminu
        </Text>
      </div>
    );
  }
  const a = at(t.dateFrom);
  const b = at(t.dateTo ?? t.dateFrom);
  const one = t.dateFrom === (t.dateTo ?? t.dateFrom);
  const past = (t.dateTo ?? t.dateFrom) < todayInWarsaw() && t.status !== 'w_toku';
  return (
    <div style={{ ...box, background: past ? 'var(--mantine-color-default-hover)' : 'var(--mantine-color-eko-light)', color: past ? 'var(--mantine-color-dimmed)' : 'var(--mantine-color-eko-light-color)' }}>
      <Text size="xs" fw={700} tt="uppercase" lh={1.2}>
        {one ? WD_SHORT[a.getUTCDay()] : `${WD_SHORT[a.getUTCDay()]}–${WD_SHORT[b.getUTCDay()]}`}
      </Text>
      <Text fw={800} lh={1.1} size={one ? '26px' : '18px'}>
        {one ? a.getUTCDate() : `${a.getUTCDate()}–${b.getUTCDate()}`}
      </Text>
      <Text size="xs" fw={600} lh={1.2}>
        {MONTH_SHORT[a.getUTCMonth()]}
        {!one && a.getUTCMonth() !== b.getUTCMonth() ? `–${MONTH_SHORT[b.getUTCMonth()]}` : ''} {b.getUTCFullYear()}
      </Text>
    </div>
  );
}

/** Wizyty (wpisy planu): dni słownie z godzinami i kto wykonuje (ekipa, osoby z przydziałów w tych dniach). */
function Visits({ t, nameOf, teamOf }: { t: TaskDto; nameOf: (id: string) => string; teamOf: (id: string | null) => string | null }) {
  const active = t.assignments.filter((a) => a.state !== 'odwolany');
  const segs = t.segments.length ? t.segments : t.dateFrom ? [{ id: 'z', dateFrom: t.dateFrom, dateTo: t.dateTo ?? t.dateFrom, timeFrom: t.timeFrom, timeTo: t.timeTo, teamId: t.teamId }] : [];
  if (!segs.length) {
    return (
      <Text size="sm" c="dimmed">
        Termin i wykonawcy — po zaplanowaniu
      </Text>
    );
  }
  return (
    <Stack gap={4}>
      {segs.slice(0, 5).map((sg) => {
        const who = [...new Set(active.filter((a) => a.day >= sg.dateFrom && a.day <= sg.dateTo).map((a) => a.userId))].map(nameOf);
        const team = teamOf(sg.teamId);
        return (
          <div key={sg.id}>
            <Group gap={6} wrap="nowrap" align="flex-start">
              <IconCalendarEvent size={16} style={{ flexShrink: 0, opacity: 0.7, marginTop: 2 }} />
              <Text size="sm">
                <Text span fw={600}>
                  {visitDays(sg.dateFrom, sg.dateTo)}
                </Text>{' '}
                <Text span c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {hours(sg)}
                </Text>
              </Text>
            </Group>
            <Group gap={6} wrap="nowrap" align="flex-start">
              <IconUsers size={16} style={{ flexShrink: 0, opacity: 0.7, marginTop: 2 }} />
              {who.length || team ? (
                <Text size="sm">
                  {team && (
                    <Text span fw={600}>
                      {team}
                      {who.length ? ': ' : ''}
                    </Text>
                  )}
                  {who.join(', ')}
                </Text>
              ) : (
                <Text size="sm" c="orange.7" fw={500}>
                  nikt nie jest przypisany
                </Text>
              )}
            </Group>
          </div>
        );
      })}
      {segs.length > 5 && (
        <Text size="xs" c="dimmed">
          … i {segs.length - 5} kolejnych wizyt
        </Text>
      )}
    </Stack>
  );
}

/** Zadania sprawy (wizje, pomiary, montaże…) z terminem, osobami i konfliktami. */
export function TasksTab({ c, me }: { c: CaseDto; me: MeDto }) {
  const typeLabel = useTaskTypeLabel();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['case-tasks', c.id], queryFn: () => api<TaskDto[]>(`/cases/${c.id}/tasks`) });
  const people = usePeople();
  const [adding, setAdding] = useState(false);
  const [realization, setRealization] = useState(false);
  const [plan, setPlan] = useState<{ tasks: TaskDto[]; index: number } | null>(null);
  // ?zadanie=<id> — otwarcie zadania z linku (np. z powiadomienia o protokole)
  const [params, setParams] = useSearchParams();
  const urlTask = params.get('zadanie');
  const [panel, setPanel] = useState<string | null>(urlTask);
  useEffect(() => {
    if (urlTask) setPanel(urlTask);
  }, [urlTask]);
  const closePanel = () => {
    setPanel(null);
    if (urlTask) setParams((p) => (p.delete('zadanie'), p), { replace: true });
    void q.refetch();
  };
  const teams = useTeams();
  const nameOf = (u: string) => people.data?.find((p) => p.id === u)?.fullName ?? '—';
  const teamOf = (id: string | null) => (id ? (teams.data?.find((x) => x.id === id)?.name ?? null) : null);
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text size="sm" c="dimmed" style={{ flex: '1 1 260px' }}>
          Realizacja wymaga spełnionych warunków startu — można ją zaplanować wcześniej (warunkowo).
        </Text>
        {(can(me, 'planowanie.edycja') || c.can.planRealization) && !c.closedAt && (
          // QA qa10302243 P10: przy wstrzymaniu realizacja zablokowana (w oknie), wizja / pomiary / inne — dalej dostępne
          <Tooltip label={c.hold ? `${executionHoldMessage(c.hold.until)}. Wizję, pomiary i inne zadania utworzysz.` : ''} disabled={!c.hold} multiline w={280}>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
              Nowe zadanie
            </Button>
          </Tooltip>
        )}
      </Group>
      <ClientPortalCard partyId={c.client.id} inCase />
      {!q.data?.length && <EmptyState compact title="Brak zadań w sprawie" hint="Wizje, pomiary i realizacje pojawią się tutaj po utworzeniu." />}
      <Stack gap="xs">
        {q.data?.map((t) => (
          <UnstyledButton key={t.id} onClick={() => setPanel(t.id)} style={{ display: 'block', width: '100%' }}>
          <Card withBorder padding="sm" className="row-link" style={{ opacity: t.status === 'anulowane' ? 0.55 : 1 }}>
            <Group align="flex-start" wrap="nowrap" gap="sm">
              <DateTile t={t} />
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Group justify="space-between" wrap="wrap" gap="xs" align="flex-start">
                  <Group gap={6} wrap="wrap" style={{ minWidth: 0 }}>
                    <Text fw={700} size="sm">
                      {typeLabel(t)}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {t.number}
                      {t.title && t.title !== typeLabel(t) ? ` · ${t.title}` : ''}
                    </Text>
                    {t.scopes.map((s) => (
                      <TradeBadge key={s.id} code={s.tradeCode} />
                    ))}
                  </Group>
                  <Group gap={4} wrap="nowrap">
                {t.conditional && (
                  <Badge size="sm" color="orange" leftSection={<IconLock size={12} />}>
                    start zablokowany
                  </Badge>
                )}
                {t.conflicts.length > 0 && (
                  <Badge size="sm" color="red" leftSection={<IconAlertTriangle size={12} />}>
                    konflikty: {t.conflicts.length}
                  </Badge>
                )}
                {!!t.fleetWarnings?.length && (
                  <Tooltip label={t.fleetWarnings.join('\n')} multiline w={320} style={{ whiteSpace: 'pre-line' }}>
                    <Badge size="sm" color="orange" variant="light" leftSection={<IconCarOff size={12} />}>
                      auto niedostępne
                    </Badge>
                  </Tooltip>
                )}
                <TaskStatusBadge status={t.status} />
                  </Group>
                </Group>
                <Visits t={t} nameOf={nameOf} teamOf={teamOf} />
              </Stack>
            </Group>
          </Card>
          </UnstyledButton>
        ))}
      </Stack>
      {adding && (
        <NewTask
          c={c}
          me={me}
          onClose={() => setAdding(false)}
          onRealization={() => {
            setAdding(false);
            setRealization(true);
          }}
          onCreated={(t) => {
            setAdding(false);
            void qc.invalidateQueries({ queryKey: ['case-tasks', c.id] });
            setPlan({ tasks: [t], index: 0 });
          }}
        />
      )}
      {realization && (
        <RealizationDialog
          caseId={c.id}
          onClose={() => setRealization(false)}
          onDone={(tasks) => {
            setRealization(false);
            void qc.invalidateQueries({ queryKey: ['case-tasks', c.id] });
            setPlan({ tasks, index: 0 });
          }}
        />
      )}
      {plan && (
        <PlanDialog
          key={plan.tasks[plan.index]!.id}
          me={me}
          task={plan.tasks[plan.index]!}
          step={{ index: plan.index + 1, total: plan.tasks.length }}
          onClose={() => {
            // seria (osobne realizacje): kolejne zadanie po zapisie albo „Pomiń”
            setPlan(plan.index + 1 < plan.tasks.length ? { ...plan, index: plan.index + 1 } : null);
            void q.refetch();
          }}
        />
      )}
      {panel && <TaskPanel me={me} taskId={panel} onClose={closePanel} />}
    </QueryState>
  );
}
